/**
 * Multi-Factor Authentication (MFA) Form Component
 * Handles TOTP/SMS code verification
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Link
} from '@mui/material';
import { confirmMfa } from '../../services/auth/authService';

export default function MfaForm({ challengeResult, onSuccess, onCancel }) {
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Determine MFA type from challenge
  const mfaType = challengeResult.challengeName === 'SMS_MFA' ? 'SMS' : 'TOTP';

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await confirmMfa(challengeResult, mfaCode);

      // Success - tokens stored automatically by authService
      if (onSuccess) {
        onSuccess(result.user);
      }
    } catch (err) {
      console.error('MFA verification error:', err);

      // User-friendly error messages
      if (err.message.includes('CodeMismatchException')) {
        setError('Invalid code. Please check and try again.');
      } else if (err.message.includes('NotAuthorizedException')) {
        setError('Code expired or invalid. Please sign in again.');
      } else {
        setError(err.message || 'Failed to verify code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}
    >
      <Typography variant="h5" component="h1" gutterBottom>
        Two-Factor Authentication
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        {mfaType === 'SMS'
          ? 'Enter the code sent to your phone'
          : 'Enter the code from your authenticator app'
        }
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Verification Code"
        value={mfaCode}
        onChange={(e) => setMfaCode(e.target.value)}
        disabled={loading}
        required
        autoFocus
        inputProps={{ maxLength: 6 }}
        helperText="Enter the 6-digit code"
      />

      <Button
        fullWidth
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ mt: 1, bgcolor: '#2d3e50', '&:hover': { bgcolor: '#3d4e60' } }}
      >
        {loading ? <CircularProgress size={24} /> : 'Verify'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Link>
      </Box>
    </Box>
  );
}
